<script setup lang="ts">
  import { computed, onUnmounted, provide, readonly, ref, watch, type PropType } from 'vue'

  import { getMarkRange, type AnyExtension } from '@tiptap/core'

  import CharacterCount from '@tiptap/extension-character-count'

  import HardBreak from '@tiptap/extension-hard-break'

  // @ts-expect-error -- TipTap heading extension is resolved by the app build, but ts-plugin misses its bundled declarations here.
  import Heading from '@tiptap/extension-heading'

  import Image from '@tiptap/extension-image'

  import Link from '@tiptap/extension-link'

  import Placeholder from '@tiptap/extension-placeholder'

  import TextStyle from '@tiptap/extension-text-style'

  import Underline from '@tiptap/extension-underline'

  import { TextSelection, type EditorState } from '@tiptap/pm/state'

  import { type EditorProps as ProseMirrorEditorProps, type EditorView } from '@tiptap/pm/view'

  import StarterKit from '@tiptap/starter-kit'

  import { useEditor as useTiptap } from '@tiptap/vue-3'

  import { useI18n } from 'vue-i18n'

  import type { ValidationError } from '@common/shared-ui-layer/types/validation.types'
  import { EDITOR_INJECTION_KEY } from './composables/useEditor'

  import { HashMentions } from './extensions/hashMentions'

  import { hashMentionsPluginKey, userMentionsPluginKey } from './extensions/mentionPluginKeys'

  import { UserMentions } from './extensions/userMentions'

  import { UnifiedMention } from './mentions/unifiedMention'

  import FormatToolbar from './tools/FormatToolbar.vue'

  import type {
    ChannelMentionPayload,
    EditorCommands,
    EditorContext,
    EventsMentionPayload,
    FormatToolName,
    UploadItemMeta,
  } from './types/editor.types'

  import type { EditorValidation } from './types/validation.types'

  const props = defineProps({
    initialContent: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    editable: {
      type: Boolean,
      default: true,
    },
    maxLength: {
      type: Number,
      default: undefined,
    },
    validation: {
      type: Object as PropType<EditorValidation>,
      default: undefined,
    },
    // Mention configuration
    mentionConfig: {
      type: Object as PropType<{
        enableUserMentions?: boolean // @ mentions
        enableHashMentions?: boolean // # mentions
      }>,
      default: () => ({
        enableUserMentions: false,
        enableHashMentions: false,
      }),
    },
    // Floating selection format toolbar (Bold / Italic / Strikethrough / Underline / Link)
    enableFormatToolbar: {
      type: Boolean,
      default: true,
    },
    // Restrict which format tools are shown; default = all. Ordering is respected.
    formatTools: {
      type: Array as PropType<FormatToolName[]>,
      default: () => ['bold', 'italic', 'strikethrough', 'underline', 'link'],
    },
    submitOnEnter: {
      type: Boolean,
      default: false,
    },
    // Ctrl/Cmd + Enter emits `submit` (plain Enter inserts a line break).
    submitOnModEnter: {
      type: Boolean,
      default: false,
    },
    // Kept for existing callers. Enter always inserts a soft <br>; Shift+Enter
    // always starts a new paragraph. This flag no longer changes that mapping.
    enterInsertsParagraph: {
      type: Boolean,
      default: false,
    },
    accessibleName: {
      type: String,
      default: '',
    },
  })

  const emit = defineEmits<{
    change: []
    validationError: [errors: ValidationError[]]
    openLinkEmbed: [selectedText: string]
    submit: []
  }>()

  const { t } = useI18n()

  /** Let @/# suggestion plugins handle Enter (select item) instead of submit-on-enter. */
  const isAnyMentionSuggestionActive = (state: EditorState) => {
    const userState = userMentionsPluginKey.getState(state) as { active?: boolean } | null | undefined
    const hashState = hashMentionsPluginKey.getState(state) as { active?: boolean } | null | undefined
    return Boolean(userState?.active || hashState?.active)
  }

  // Initialize Tiptap editor
  // Disable StarterKit's bundled Heading and HardBreak because we re-register
  // custom variants below. Leaving either enabled in StarterKit causes two
  // plugins to share the same PluginKey, which throws:
  //   RangeError: Adding different instances of a keyed plugin (plugin$)
  const extensions: AnyExtension[] = [
    StarterKit.configure({ heading: false, hardBreak: false }),
    TextStyle,
    Underline,
    Image.configure({ inline: true, allowBase64: true }),
    Link.configure({
      openOnClick: true,
    }),
    CharacterCount.configure({
      limit: props.maxLength,
    }),
    // Re-add Heading without input rules so "# + space" won't auto-convert
    Heading.extend({
      addInputRules() {
        return []
      },
    }),
    Placeholder.configure({
      placeholder: ({ editor, node }) => {
        // show a placeholder only while the ENTIRE editor has no text
        const isDocEmpty = editor.state.doc.textContent.trim().length === 0
        if (isDocEmpty && node.type.name === 'paragraph') {
          return props.placeholder || t('common.editor.placeholder.default')
        }
        return ''
      },
      showOnlyCurrent: true, // <-- key: only the focused empty node
      showOnlyWhenEditable: true,
      emptyNodeClass: 'is-empty',
      emptyEditorClass: 'is-editor-empty',
    }),
    HardBreak.extend({
      addKeyboardShortcuts() {
        return {
          Enter: () => {
            const { state } = this.editor
            const { $from } = state.selection

            // Inside a list item (bulletList / orderedList / taskList), defer to
            // ListItem's default Enter which splits the item into a new bullet.
            // Overriding Enter globally otherwise breaks list continuation.
            for (let depth = $from.depth; depth > 0; depth -= 1) {
              const nodeName = $from.node(depth).type.name
              if (nodeName === 'listItem' || nodeName === 'taskItem') {
                return false
              }
            }

            // If the line starts with just a '#', do nothing (disable "# + Enter" formatting)
            const textBefore = $from.parent.textBetween(0, $from.parentOffset)
            if (/^#\s*$/.test(textBefore)) {
              return true
            }
            return this.editor.commands.setHardBreak()
          },
          'Shift-Enter': () => this.editor.commands.splitBlock(),
        }
      },
    }),
    // Mention nodes MUST be registered before the editor is created. Mutating
    // the extensions array after useTiptap() leads to inconsistent plugin state
    // across HMR reloads and can surface as duplicate-plugin-key errors.
    UnifiedMention,
    ...(props.mentionConfig?.enableUserMentions ? [UserMentions] : []),
    ...(props.mentionConfig?.enableHashMentions ? [HashMentions] : []),
  ]

  const editorProps: ProseMirrorEditorProps = {
    attributes: {
      'aria-label': props.accessibleName || undefined,
      'aria-multiline': 'true',
      role: 'textbox',
    },
    handleKeyDown: (view: EditorView, event: KeyboardEvent) => {
      // Ctrl/Cmd + Enter submits (plain Enter is a line break)
      if (props.submitOnModEnter && event.key === 'Enter' && (event.metaKey || event.ctrlKey) && !event.isComposing) {
        if (isAnyMentionSuggestionActive(view.state)) {
          return false
        }
        event.preventDefault()
        emit('submit')
        return true
      }

      if (props.submitOnEnter && event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
        if (isAnyMentionSuggestionActive(view.state)) {
          return false
        }
        event.preventDefault()
        emit('submit')
        return true
      }

      // If typing at the end of a link, clear stored marks so new text is outside the link
      if (event.key.length === 1 || event.key === ' ' || event.key === 'Spacebar') {
        const editor = tiptap.value
        if (!editor) return false
        const { state } = editor
        const linkType = state.schema.marks.link
        const pos = state.selection.from
        // Look at position before the cursor to detect current link range
        const $prevPos = state.doc.resolve(Math.max(0, pos - 1))
        const range = getMarkRange($prevPos, linkType)
        if (range && pos === range.to) {
          // Clear stored marks so the next character won't inherit the link mark
          editor.view.dispatch(state.tr.setStoredMarks([]))
          // Do not prevent default - allow normal character insertion
          return false
        }
      }
      return false
    },
  }

  const tiptap = useTiptap({
    extensions,
    content: props.initialContent,
    editable: props.editable,
    editorProps,
    parseOptions: {
      preserveWhitespace: 'full',
    },
    onUpdate: () => {
      emit('change')
    },
    onCreate: () => {},
  })

  // Upload state
  const isUploading = ref(false)

  const uploads = ref<Map<string, UploadItemMeta>>(new Map())

  // Focus state
  const isFocused = ref(false)

  // Minimal commands facade
  const commands: EditorCommands = {
    insertEmoji: (emoji: string) => tiptap.value?.chain().focus().insertContent(emoji).run(),
    image: {
      insertBase64: (src: string, alt?: string) => tiptap.value?.chain().focus().setImage({ src, alt }).run(),
      insertUrl: (src: string, alt?: string) => tiptap.value?.chain().focus().setImage({ src, alt }).run(),
    },
    link: {
      set: ({ href, text, target, noFollow }) => {
        const e = tiptap.value
        if (!e) return
        e.chain()
          .focus()
          .extendMarkRange('link')
          .setLink({ href, target, rel: noFollow ? 'nofollow' : undefined })
          .command(({ tr }) => {
            if (text) tr.insertText(text)
            return true
          })
          .command(({ tr, state }) => {
            // Insert an invisible zero-width space after the link so typing starts outside the link
            const { to } = state.selection
            tr.insertText('\u200B', to)
            // Place the cursor after the inserted ZWS
            tr.setSelection(TextSelection.near(tr.doc.resolve(to + 1)))
            // Clear stored marks so the next character doesn't inherit the link mark
            tr.setStoredMarks([])
            return true
          })
          .run()
      },
      unset: () => tiptap.value?.chain().focus().unsetLink().run(),
    },
    insertContent: (html: string) => tiptap.value?.chain().focus().insertContent(html).run(),
    mention: {
      insertChannel: (channel: ChannelMentionPayload) => {
        const editor = tiptap.value
        if (!editor) return

        // Use unified mention node
        const inserted = editor.commands.insertMention({
          id: channel.id,
          name: channel.name,
          label: channel.name,
          slug: channel.slug,
          icon: channel.icon,
          iconType: channel.iconType,
          type: 'channel',
        })

        if (inserted) {
          editor.commands.insertContent(' ')
        }
      },
      insertEvents: (event: EventsMentionPayload) => {
        const editor = tiptap.value
        if (!editor) return

        // Use unified mention node
        const inserted = editor.commands.insertMention({
          id: event.id,
          label: event.label,
          name: event.label,
          timestamp: event.timestamp,
          type: 'event',
        })

        if (inserted) {
          editor.commands.insertContent(' ')
        }
      },
      // insertHash removed - HashMentions extension handles its own insertion
    },
  }

  // Provide context
  const ctx: EditorContext = {
    editor: computed(() => tiptap.value || null),
    commands,
    uploads: {
      isUploading: computed(() => isUploading.value),
      items: uploads,
    },
  }

  const handleAddLink = () => {
    if (!tiptap.value) return
    const editor = tiptap.value
    const { from, to } = editor.state.selection
    const selectedText = editor.state.doc.textBetween(from, to, ' ')
    emit('openLinkEmbed', selectedText)
  }

  onUnmounted(() => {
    tiptap.value?.destroy()
  })

  // No live syncing from a prop. Consumers should call exposed setters explicitly if needed.

  watch(
    () => props.editable,
    val => tiptap.value?.setEditable(val)
  )

  // Set up focus/blur listeners when editor is ready
  watch(
    () => tiptap.value,
    editor => {
      if (!editor) return

      // Define event handlers as regular functions (not arrow functions) for proper cleanup
      function handleFocus() {
        isFocused.value = true
      }

      function handleBlur() {
        isFocused.value = false
      }

      editor.on('focus', handleFocus)
      editor.on('blur', handleBlur)

      function cleanup() {
        if (editor) {
          editor.off('focus', handleFocus)
          editor.off('blur', handleBlur)
          editor.off('destroy', handleDestroy)
        }
      }

      // Register destroy handler - cleanup will be called when editor is destroyed
      function handleDestroy() {
        cleanup()
      }

      editor.on('destroy', handleDestroy)

      // Return cleanup function for watch callback - Vue will call this when watch is cleaned up
      return cleanup
    },
    { immediate: true }
  )

  provide(EDITOR_INJECTION_KEY, ctx)

  // Expose getters/setters for consumers (pages) to use on demand
  defineExpose({
    getHTML: () => tiptap.value?.getHTML() || '',
    getText: () => tiptap.value?.getText() || '',
    setContent: (html: string) => tiptap.value?.commands.setContent(html),
    getEditor: () => tiptap.value,
    getCharacterCount: () => tiptap.value?.storage.characterCount.characters() || 0,
    isFocused: readonly(isFocused),
  })
</script>

<template>
  <div class="bg-background">
    <FormatToolbar v-if="tiptap && props.enableFormatToolbar && isFocused" :tools="props.formatTools" @add-link="handleAddLink" />

    <slot />
  </div>
</template>

<style scoped>
  /* Editor-specific styles - WYSIWYG styles live in @common/shared-ui-layer/assets/rich-text.css */
  :deep(.ProseMirror) {
    outline: none;
    min-height: inherit;
  }

  /* Placeholder styling - TipTap Placeholder extension */
  :deep(.ProseMirror p.is-editor-empty:first-child::before) {
    color: var(--cp-font-500); /* gray-500, use variable */
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
    font-family: Inter, sans-serif;
    font-weight: 400;
    font-size: 16px;
  }

  :deep(.ProseMirror .is-empty::before) {
    color: var(--cp-font-500); /* gray-500, use variable */
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
    font-family: Inter, sans-serif;
    font-weight: 400;
    font-size: 16px;
  }

  :deep(.ProseMirror.is-editor-empty::before) {
    color: var(--cp-font-500); /* gray-500, use variable */
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
    font-family: Inter, sans-serif;
    font-weight: 400;
    font-size: 16px;
  }

  /* Alternative approach for TipTap placeholder */
  :deep(.ProseMirror[data-placeholder]:empty::before) {
    color: var(--cp-font-500);
    content: attr(data-placeholder);
    font-family: Inter, sans-serif;
    font-weight: 400;
    font-size: 16px;
    pointer-events: none;
    position: absolute;
  }
</style>

<!-- Non-scoped: the format bubble menu is portaled by tippy, so scoped :deep()
     can't reach it. The theme attribute keeps this scoped to our bubble menu only. -->
<style>
  .tippy-box[data-theme~='cp-format-toolbar'] {
    background-color: transparent;
    box-shadow: none;
    padding: 0;
  }
  .tippy-box[data-theme~='cp-format-toolbar'] .tippy-content {
    padding: 0;
  }
  .tippy-box[data-theme~='cp-format-toolbar'] .tippy-arrow {
    display: none;
  }
</style>
